import type { MaybeRefOrGetter } from 'vue'
import type { ConfiguredImageProviders, ImageOptions } from '@nuxt/image'
import { toValue, useHead, useImage, useRequestEvent } from '#imports'

// @nuxt/image types `getSizes` against the default provider; the callers here name theirs.
type PreloadImageOptions = ImageOptions<keyof ConfiguredImageProviders>

export interface UseImagePreloadOptions {
    src: MaybeRefOrGetter<string | null | undefined>
    /** The `<NuxtImg>`'s `sizes` string, verbatim, so the candidates match its srcset. */
    sizes: string
    provider?: PreloadImageOptions['provider']
    preset?: MaybeRefOrGetter<string | undefined>
    /** The `<NuxtImg>`'s width/height/quality/fit, with the same value types it received. */
    modifiers?: MaybeRefOrGetter<PreloadImageOptions['modifiers']>
}

/**
 * SSR `<link rel="preload" as="image">` for the responsive `<NuxtImg>` that is the page's
 * LCP candidate, so the browser starts the fetch from the head instead of waiting until
 * the parser reaches the markup.
 *
 * One per page: the first candidate set up wins, which on the server is the first in
 * document order. spilnu's lobby renders the content hero above the rotator, and two
 * `fetchpriority=high` preloads would split the bandwidth the LCP image needs; the loop
 * rotator's clone of its first tile is covered the same way. unhead cannot pick for us:
 * links sharing a key merge, and the last one wins.
 *
 * Not `<NuxtImg preload>`: that only emits `imagesrcset` for density (`2x`) srcsets and
 * preloads the widest width candidate of a `sizes` image, which the `<img>` then does not
 * pick — a second full download. Server-only like NuxtImg's own preload; the client never
 * re-adds the link. `isServer` exists because vitest defines `import.meta.server` as false
 * for every test. Data URLs (preview pages) are skipped: nothing to fetch ahead.
 */
export function useImagePreload(options: UseImagePreloadOptions, isServer = import.meta.server): void {
    if (!isServer) return

    const src = toValue(options.src)
    if (!src || src.startsWith('data:')) return

    const event = useRequestEvent()
    if (event?.context.imagePreloadClaimed) return
    if (event) event.context.imagePreloadClaimed = true

    const $img = useImage()
    // The provider writes its operations in key order, so the modifiers must be built the
    // way @nuxt/image's `useImageProps` builds the <NuxtImg>'s (not exported): same fields,
    // same order, same default quality. `{ height, width }` gives `h=…,w=…` URLs the img
    // never requests.
    const { width, height, format, quality, background, fit, ...rest } = toValue(options.modifiers) ?? {}
    const { srcset, sizes, src: href } = $img.getSizes(src, {
        provider: options.provider,
        preset: toValue(options.preset),
        sizes: options.sizes,
        modifiers: { ...rest, width, height, format, quality: quality || $img.options.quality, background, fit },
    } as ImageOptions)

    useHead({
        link: [{
            key: 'lcp-image-preload',
            rel: 'preload',
            as: 'image',
            href,
            imagesrcset: srcset,
            imagesizes: sizes,
            fetchpriority: 'high',
        }],
    })
}
